/**
 * Send web crashes somewhere a person will actually see them.
 *
 * Crashlytics (src/lib/crashlytics.ts) covers the native apps, but its plugin
 * has no browser implementation, so every JavaScript error on zuzuforyou.com
 * ended in the visitor's console and nowhere else. A screen throwing for every
 * member looked, from the outside, exactly like a quiet day — which is how a
 * week of breakage went unnoticed until signing in stopped working.
 *
 * This posts to the report-client-error edge function, which records it. The
 * watchdog decides what deserves an email; nothing here alerts directly.
 *
 * Fire-and-forget in the strictest sense: reporting an error must never throw,
 * never block a render, and never make a bad situation worse. Every failure
 * path here ends in a silent return.
 */
// The auth-only client, NOT @/integrations/supabase/client. This module is
// reached from crashlytics.ts, which main.tsx imports eagerly — pulling in the
// full client here would ship GoTrueClient + Realtime + Postgrest + Storage to
// every anonymous marketing-page visit, undoing exactly what authClient.ts
// exists to avoid. Reading a session is safe on this instance; only
// session-MUTATING calls must stay on the one useZuzuSession listens to.
import { authClient } from "@/integrations/supabase/authClient";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/report-client-error`;

// A render loop can throw thousands of times a second. The server deduplicates
// too, but the first line of defence has to be here: without it a single bad
// deploy would have every open browser hammering the endpoint, turning our own
// error reporting into a second outage.
const REPEAT_SUPPRESSION_MS = 5 * 60 * 1000;
const MAX_REPORTS_PER_SESSION = 25;

const lastSentAt = new Map<string, number>();
let sentThisSession = 0;

/** Cheap client-side grouping key. The server computes its own, properly. */
function localKey(message: string, stack: string): string {
  return `${message}|${stack.split("\n")[1]?.trim() ?? ""}`.slice(0, 300);
}

function shouldSend(key: string): boolean {
  if (sentThisSession >= MAX_REPORTS_PER_SESSION) return false;
  const previous = lastSentAt.get(key);
  if (previous && Date.now() - previous < REPEAT_SUPPRESSION_MS) return false;
  // Bound the map as well — an error whose message embeds a changing value
  // (an id, a timestamp) produces a new key every time.
  if (lastSentAt.size > 100) lastSentAt.clear();
  lastSentAt.set(key, Date.now());
  sentThisSession += 1;
  return true;
}

/**
 * Report one error. Safe to call from anywhere, including inside a catch that
 * is already handling something else going wrong.
 */
export function reportClientError(
  error: unknown,
  context: { source?: string } = {},
): void {
  try {
    if (typeof window === "undefined" || !import.meta.env.VITE_SUPABASE_URL) {
      return;
    }

    const err =
      error instanceof Error ? error : new Error(String(error ?? "unknown"));
    const message = err.name ? `${err.name}: ${err.message}` : err.message;
    const stack = err.stack ?? "";

    if (!shouldSend(localKey(message, stack))) return;

    // The member's token when there is one, so a report can be traced back to
    // who hit it — but never required. getSession reads local storage only, so
    // this stays synchronous-ish and works even when the network is the thing
    // that's broken.
    void authClient
      .getSession()
      .then(({ data }) => data.session?.access_token)
      .catch(() => undefined)
      .then((token) =>
        fetch(ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: message.slice(0, 2000),
            stack: stack.slice(0, 8000),
            source: context.source ?? "unknown",
            url: window.location.href.slice(0, 1000),
          }),
          // Survive the page being torn down by the very crash we're reporting.
          keepalive: true,
        }),
      )
      .catch(() => {
        /* reporting is best-effort; never surface a failure to report */
      });
  } catch {
    /* never let the reporter itself throw */
  }
}
